import { useQuery } from '@tanstack/react-query';

import { EdgeConfiguration } from '@cast/types-omni';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const EDGE_CONFIGURATION_QUERY_KEY = 'edge-configuration';

export const getEdgeConfigurationQueryKey = ({
  organizationId,
  clusterId,
  edgeLocationId,
  edgeConfigurationId,
}: {
  organizationId?: string;
  clusterId?: string;
  edgeLocationId?: string;
  edgeConfigurationId?: string;
}) => [
  EDGE_CONFIGURATION_QUERY_KEY,
  organizationId,
  clusterId,
  edgeLocationId,
  edgeConfigurationId,
];

type UseGetEdgeConfiguration = {
  isLoading: boolean;
  error?: unknown;
  edgeConfiguration?: EdgeConfiguration;
  refetch: () => void;
};

export const useGetEdgeConfigurationQuery = ({
  clusterId,
  edgeLocationId,
  edgeConfigurationId,
  enabled = true,
}: {
  clusterId: string | undefined;
  edgeLocationId: string | undefined;
  edgeConfigurationId: string | undefined;
  enabled?: boolean;
}): UseGetEdgeConfiguration => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id;

  const {
    isLoading,
    data: edgeConfiguration,
    error,
    refetch,
  } = useQuery({
    queryKey: getEdgeConfigurationQueryKey({
      organizationId,
      clusterId,
      edgeLocationId,
      edgeConfigurationId,
    }),
    queryFn: async () => {
      const { data } = await apiClient.omni.getEdgeConfiguration({
        organizationId: organizationId!,
        clusterId: clusterId!,
        edgeLocationId: edgeLocationId!,
        id: edgeConfigurationId!,
      });
      return data;
    },
    enabled:
      enabled &&
      !!clusterId &&
      !!organizationId &&
      !!edgeLocationId &&
      !!edgeConfigurationId,
  });

  return {
    isLoading,
    edgeConfiguration,
    error,
    refetch,
  };
};
